import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Checkbox,
  Divider,
  LinearProgress,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Snackbar,
  Collapse,
} from '@mui/material';
import {
  CheckCircle,
  RadioButtonUnchecked,
  Info,
  Notes,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { apiFetch } from '../../services/apiFetch';

interface ChecklistStep {
  id: string;
  title: string;
  description: string;
  category: 'pre_processing' | 'calculation' | 'review' | 'distribution';
  isRequired: boolean;
  sortOrder: number;
  isCompleted: boolean;
  completedBy?: { id: string; email: string };
  completedAt?: string;
  completionNote?: string;
}

interface ChecklistProgress {
  total: number;
  completed: number;
  requiredTotal: number;
  requiredDone: number;
  percent: number;
}

interface Checklist {
  id: string;
  payrollRunId: string;
  status: 'in_progress' | 'complete';
  notes: string;
  completedAt?: string;
  steps: ChecklistStep[];
  progress: ChecklistProgress;
}

interface PayrollChecklistProps {
  payrollRunId: string;
  periodName: string;
}

const CATEGORY_CONFIG: Record<string, { label: string; color: 'primary' | 'secondary' | 'warning' | 'success' }> = {
  pre_processing: { label: 'Pre-Processing', color: 'primary' },
  calculation:    { label: 'Calculation',    color: 'secondary' },
  review:         { label: 'Review & Sign-off', color: 'warning' },
  distribution:   { label: 'Distribution',  color: 'success' },
};

const CATEGORY_ORDER = ['pre_processing', 'calculation', 'review', 'distribution'];

const PayrollChecklist: React.FC<PayrollChecklistProps> = ({ payrollRunId, periodName }) => {
  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Note dialog
  const [noteDialog, setNoteDialog] = useState<{ open: boolean; stepId: string; note: string } | null>(null);
  const [savingNote, setSavingNote] = useState(false);

  // Checklist notes
  const [showChecklistNotes, setShowChecklistNotes] = useState(false);
  const [checklistNotes, setChecklistNotes] = useState('');
  const [savingChecklistNotes, setSavingChecklistNotes] = useState(false);

  // Collapsed categories
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  // Snackbar
  const [snack, setSnack] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({
    open: false, msg: '', sev: 'success',
  });

  const fetchChecklist = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiFetch(`/api/payroll-checklist/run/${payrollRunId}`);
      if (!res.ok) throw new Error('Failed to load checklist');
      const data: Checklist = await res.json();
      setChecklist(data);
      setChecklistNotes(data.notes || '');
    } catch (err: any) {
      setError(err.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [payrollRunId]);

  useEffect(() => { fetchChecklist(); }, [fetchChecklist]);

  const toggleStep = async (step: ChecklistStep) => {
    if (!checklist) return;
    const newVal = !step.isCompleted;

    // Optimistic update
    setChecklist((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        steps: prev.steps.map((s) => s.id === step.id ? { ...s, isCompleted: newVal } : s),
      };
    });

    try {
      await apiFetch(`/api/payroll-checklist/${checklist.id}/step/${step.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isCompleted: newVal }),
      });
      await fetchChecklist(); // refresh to get progress + completedBy
    } catch {
      // Revert
      setChecklist((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          steps: prev.steps.map((s) => s.id === step.id ? { ...s, isCompleted: step.isCompleted } : s),
        };
      });
      setSnack({ open: true, msg: 'Failed to update step', sev: 'error' });
    }
  };

  const saveStepNote = async () => {
    if (!checklist || !noteDialog) return;
    setSavingNote(true);
    try {
      await apiFetch(`/api/payroll-checklist/${checklist.id}/step/${noteDialog.stepId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isCompleted: true, completionNote: noteDialog.note }),
      });
      setNoteDialog(null);
      await fetchChecklist();
      setSnack({ open: true, msg: 'Note saved', sev: 'success' });
    } catch {
      setSnack({ open: true, msg: 'Failed to save note', sev: 'error' });
    } finally {
      setSavingNote(false);
    }
  };

  const saveChecklistNotes = async () => {
    if (!checklist) return;
    setSavingChecklistNotes(true);
    try {
      await apiFetch(`/api/payroll-checklist/${checklist.id}/notes`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: checklistNotes }),
      });
      setSnack({ open: true, msg: 'Notes saved', sev: 'success' });
    } catch {
      setSnack({ open: true, msg: 'Failed to save notes', sev: 'error' });
    } finally {
      setSavingChecklistNotes(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!checklist) return null;

  const { progress, steps } = checklist;
  const grouped = CATEGORY_ORDER.reduce<Record<string, ChecklistStep[]>>((acc, cat) => {
    acc[cat] = steps.filter((s) => s.category === cat).sort((a, b) => a.sortOrder - b.sortOrder);
    return acc;
  }, {});

  const isComplete = checklist.status === 'complete';

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Payroll Close Checklist
          </Typography>
          <Typography variant="body2" color="text.secondary">{periodName}</Typography>
        </Box>
        <Chip
          label={isComplete ? '✅ Complete' : `${progress.completed} / ${progress.total} done`}
          color={isComplete ? 'success' : 'default'}
          variant={isComplete ? 'filled' : 'outlined'}
        />
      </Box>

      {/* Progress bar */}
      <Box mb={3}>
        <Box display="flex" justifyContent="space-between" mb={0.5}>
          <Typography variant="caption" color="text.secondary">
            {progress.completed} of {progress.total} steps completed
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {progress.requiredDone}/{progress.requiredTotal} required
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress.percent}
          color={isComplete ? 'success' : progress.percent >= 50 ? 'warning' : 'primary'}
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Box>

      {isComplete && (
        <Alert severity="success" sx={{ mb: 2 }}>
          🎉 All required steps completed! This payroll run is ready to lock.
          {checklist.completedAt && ` Completed ${new Date(checklist.completedAt).toLocaleString()}.`}
        </Alert>
      )}

      {/* Steps by category */}
      {CATEGORY_ORDER.map((cat) => {
        const catSteps = grouped[cat];
        if (!catSteps.length) return null;
        const cfg = CATEGORY_CONFIG[cat];
        const catDone = catSteps.filter((s) => s.isCompleted).length;
        const isCollapsed = collapsed[cat];

        return (
          <Paper key={cat} variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
            {/* Category header */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              px={2}
              py={1}
              sx={{ cursor: 'pointer', bgcolor: 'action.hover' }}
              onClick={() => setCollapsed((prev) => ({ ...prev, [cat]: !prev[cat] }))}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Chip label={cfg.label} color={cfg.color} size="small" />
                <Typography variant="body2" color="text.secondary">
                  {catDone} / {catSteps.length}
                </Typography>
              </Box>
              <IconButton size="small">
                {isCollapsed ? <ExpandMore /> : <ExpandLess />}
              </IconButton>
            </Box>

            <Collapse in={!isCollapsed}>
              {catSteps.map((step, idx) => (
                <React.Fragment key={step.id}>
                  {idx > 0 && <Divider />}
                  <Box
                    display="flex"
                    alignItems="flex-start"
                    px={2}
                    py={1.5}
                    gap={1}
                    sx={{
                      bgcolor: step.isCompleted ? 'success.50' : 'transparent',
                      transition: 'background-color 0.2s',
                    }}
                  >
                    {/* Checkbox */}
                    <Checkbox
                      checked={step.isCompleted}
                      onChange={() => toggleStep(step)}
                      icon={<RadioButtonUnchecked />}
                      checkedIcon={<CheckCircle color="success" />}
                      sx={{ mt: '-2px', p: 0.5 }}
                    />

                    {/* Content */}
                    <Box flex={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ textDecoration: step.isCompleted ? 'line-through' : 'none', color: step.isCompleted ? 'text.disabled' : 'text.primary' }}
                        >
                          {step.title}
                        </Typography>
                        {!step.isRequired && (
                          <Chip label="Optional" size="small" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
                        )}
                      </Box>

                      <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
                        {step.description}
                      </Typography>

                      {step.isCompleted && step.completedBy && (
                        <Typography variant="caption" color="success.main" display="block" mt={0.5}>
                          ✓ Marked by {step.completedBy.email}
                          {step.completedAt && ` · ${new Date(step.completedAt).toLocaleString()}`}
                        </Typography>
                      )}

                      {step.completionNote && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontStyle: 'italic' }}>
                          "{step.completionNote}"
                        </Typography>
                      )}
                    </Box>

                    {/* Add note button */}
                    <Tooltip title="Add completion note">
                      <IconButton
                        size="small"
                        onClick={() => setNoteDialog({ open: true, stepId: step.id, note: step.completionNote || '' })}
                      >
                        <Notes fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </React.Fragment>
              ))}
            </Collapse>
          </Paper>
        );
      })}

      {/* Checklist notes */}
      <Box mt={2}>
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          sx={{ cursor: 'pointer', mb: 1 }}
          onClick={() => setShowChecklistNotes((v) => !v)}
        >
          <Notes fontSize="small" color="action" />
          <Typography variant="body2" color="text.secondary">Run notes</Typography>
          {showChecklistNotes ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </Box>
        <Collapse in={showChecklistNotes}>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Add any notes for this payroll run..."
            value={checklistNotes}
            onChange={(e) => setChecklistNotes(e.target.value)}
            size="small"
          />
          <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={saveChecklistNotes} disabled={savingChecklistNotes}>
            {savingChecklistNotes ? 'Saving...' : 'Save Notes'}
          </Button>
        </Collapse>
      </Box>

      {/* Step note dialog */}
      <Dialog open={!!noteDialog?.open} onClose={() => setNoteDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Completion Note</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="e.g. Variance of $200 for John approved by Finance on 15 Apr"
            value={noteDialog?.note || ''}
            onChange={(e) => setNoteDialog((prev) => prev ? { ...prev, note: e.target.value } : prev)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveStepNote} disabled={savingNote}>
            {savingNote ? 'Saving...' : 'Save & Mark Complete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.sev} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PayrollChecklist;
